import * as React from 'react'
import clsx from 'clsx'
import { FadeProps, Fade } from '../Fade'
import { TransitionProps } from '../transitions'
import { SxProps, sx as sxFunc } from '../sx'
import style from './index.module.scss'

export interface BackdropClasses {
  /** Styles applied to the root element. */
  root: string
  /** Styles applied to the root element if `invisible={true}`. */
  invisible: string
}

export interface BackdropProps extends Omit<FadeProps, 'children'> {
  /**
   * The content of the component.
   */
  children?: React.ReactNode
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<BackdropClasses>
  /**
   * If `true`, the backdrop is invisible.
   * It can be used when rendering a popover or a custom select component.
   * @default false
   */
  invisible?: boolean
  /**
   * If `true`, the component is shown.
   */
  open: boolean
  /**
   * The extra props for the slot components.
   * You can override the existing props or add new ones.
   *
   * This prop is an alias for the `componentsProps` prop, which will be deprecated in the future.
   *
   * @default {}
   */
  slotProps?: {
    root?: React.HTMLAttributes<HTMLDivElement>
  }
  /**
   * The components used for each slot inside.
   *
   * This prop is an alias for the `components` prop, which will be deprecated in the future.
   *
   * @default {}
   */
  slots?: {
    root?: React.ElementType
  }
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
  /**
   * The duration for the transition, in milliseconds.
   * You may specify a single timeout for all transitions, or individually with an object.
   */
  transitionDuration?: TransitionProps['timeout']
}

export const Backdrop = React.forwardRef<HTMLDivElement, BackdropProps>(
  function Backdrop(props, ref) {
    const {
      children,
      className,
      classes,
      invisible = false,
      open,
      slotProps = {},
      slots = {},
      transitionDuration,
      sx,
      // @ts-ignore
      ownerState: _,
      ...other
    } = props

    const rootSlotProps = slotProps.root
    const Component = slots.root ?? 'div'

    return (
      <Fade in={open} timeout={transitionDuration} {...other}>
        <Component
          aria-hidden
          {...rootSlotProps}
          className={clsx(
            style.root,
            classes?.root,
            invisible && [style.invisible, classes?.invisible],
            className,
            rootSlotProps?.className,
            sxFunc(sx)
          )}
          ref={ref}
        >
          {children}
        </Component>
      </Fade>
    )
  }
)
